import React from "react";
import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: var(--border-radius);
  border: none;
  background: var(--gold-gradient);
  background-size: 200% auto;
  color: #000000;
  cursor: pointer;
  transition: var(--transition);
  box-shadow: var(--gold-shadow);
  position: relative;
  overflow: hidden;
  letter-spacing: 1px;

  @media (max-width: 576px) {
    padding: 0.7rem 1.2rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.65rem 1rem;
    font-size: 0.9rem;
    border-radius: 6px;
  }

  &:hover {
    background-position: right center;
    box-shadow: 0 0 15px rgba(212, 175, 55, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: var(--gold-shadow);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #3a3a3a;
    color: #aaa;
    transform: none;
    box-shadow: none;
  }

  &:after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    animation: ${shimmer} 6s infinite linear;
    pointer-events: none;
    z-index: 1;
    opacity: 0.4;
  }

  ${(props) =>
    props.variant === "secondary" &&
    `
    background: linear-gradient(90deg, #313131, #4a4a4a, #313131);
    background-size: 200% auto;
    color: var(--primary-color);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
    
    &:hover {
      background-position: right center;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
    }
    
    &:active {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
    }
  `}

  ${(props) =>
    props.variant === "outlined" &&
    `
    background: transparent;
    color: var(--primary-color);
    border: 2px solid var(--primary-color);
    box-shadow: none;
    
    &:hover {
      background-color: rgba(212, 175, 55, 0.05);
      box-shadow: none;
    }
    
    &:active {
      background-color: rgba(212, 175, 55, 0.1);
      box-shadow: none;
    }
    
    &:after {
      display: none;
    }
  `}
  
  ${(props) =>
    props.fullWidth &&
    `
    width: 100%;
  `}

  svg {
    margin-right: ${(props) => (props.iconOnly ? "0" : "0.5rem")};
    font-size: 1.2rem;

    @media (max-width: 576px) {
      font-size: 1.1rem;
    }
  }
`;

const Button = ({ children, variant, fullWidth, iconOnly, ...props }) => {
  return (
    <StyledButton
      variant={variant}
      fullWidth={fullWidth}
      iconOnly={iconOnly}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
